// apps/accounts/src/pages/materials/Suppliers.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, Search, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const Suppliers = () => {
  const { role } = useAuth();
  const canManage = ['admin', 'accounts'].includes(role);

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '', contactPerson: '', phone: '', email: '',
    gst: '', pan: '', address: '', bankName: '', accountNumber: '', ifsc: '', notes: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = async () => {
    try {
      const records = await pb.collection('suppliers').getFullList({
        sort: 'companyName',
        requestKey: null,
      });
      setSuppliers(records);
    } catch (err) { if (err.isAbort) return; toast.error('Failed to load suppliers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try { await pb.collection('suppliers').delete(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const openModal = (supplier = null) => {
    setEditing(supplier);
    setFormData(supplier ? {
      companyName: supplier.companyName || '',
      contactPerson: supplier.contactPerson || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      gst: supplier.gst || '',
      pan: supplier.pan || '',
      address: supplier.address || '',
      bankName: supplier.bankName || '',
      accountNumber: supplier.accountNumber || '',
      ifsc: supplier.ifsc || '',
      notes: supplier.notes || '',
    } : {
      companyName: '', contactPerson: '', phone: '', email: '',
      gst: '', pan: '', address: '', bankName: '', accountNumber: '', ifsc: '', notes: '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = { ...formData };
      Object.keys(data).forEach(k => { if (data[k] === '') data[k] = null; });
      if (editing) {
        await pb.collection('suppliers').update(editing.id, data);
        toast.success('Supplier updated');
      } else {
        await pb.collection('suppliers').create(data);
        toast.success('Supplier created');
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" /></div>;

  const filtered = suppliers.filter(s =>
    s.companyName?.toLowerCase().includes(search.toLowerCase()) ||
    s.contactPerson?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Material Suppliers</h1>
          <p className="text-sm text-slate-500">Manage material suppliers</p>
        </div>
        {canManage && (
          <Button onClick={() => openModal(null)} className="bg-amber-500 hover:bg-amber-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Supplier
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm border">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by company or contact..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button variant="outline" onClick={() => setSearch('')}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Company</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Contact</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Phone</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">GST</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? <tr><td colSpan="5" className="px-4 py-8 text-center">No suppliers</td></tr> : filtered.map(s => (
                <tr key={s.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium">{s.companyName}</td>
                  <td className="px-4 py-3">{s.contactPerson || '—'}</td>
                  <td className="px-4 py-3">{s.phone || '—'}</td>
                  <td className="px-4 py-3">{s.gst || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => openModal(s)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"><Edit className="w-4 h-4" /></button>
                      {canManage && (
                        <button onClick={() => handleDelete(s.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Edit Supplier' : 'New Supplier'}</h2>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name *</label>
                  <Input name="companyName" value={formData.companyName} onChange={handleChange} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Person</label>
                  <Input name="contactPerson" value={formData.contactPerson} onChange={handleChange} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">GST</label>
                  <Input name="gst" value={formData.gst} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">PAN</label>
                  <Input name="pan" value={formData.pan} onChange={handleChange} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input name="address" value={formData.address} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Bank Name</label>
                  <Input name="bankName" value={formData.bankName} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Account Number</label>
                  <Input name="accountNumber" value={formData.accountNumber} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">IFSC</label>
                  <Input name="ifsc" value={formData.ifsc} onChange={handleChange} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Input name="notes" value={formData.notes} onChange={handleChange} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editing ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;